import Box from '@fuegokit/react/components/Box/Box'
import Tooltip from '@fuegokit/react/components/Tooltip/Tooltip'
import themeGet from '@fuegokit/react/utils/theme-get'
import styled, {keyframes} from 'styled-components'

// animations
const slideUpAndFade = keyframes`  
  from {
    opacity: 0;
    transform: translateY(2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`
const slideRightAndFade = keyframes`
  from {
    opacity: 0;
    transform: translateX(-2px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`
const slideDownAndFade = keyframes`
  from {
    opacity: 0;
    transform: translateY(-2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`
const slideLeftAndFade = keyframes`
  from {
    opacity: 0;
    transform: translateX(2px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`
// basic styled
export const StyledTooltipContent = styled(Tooltip.Content)`
  border-radius: ${themeGet('radii.2')};
  padding: ${themeGet('space.2')} ${themeGet('space.4')};
  font-size: ${themeGet('fontSizes.3')};
  line-height: 1;
  color: ${themeGet('colors.text.accent.teal.bolder')};
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  /* box-shadow: hsl(206 22% 7% / 35%) 0px 10px 38px -10px, hsl(206 22% 7% / 20%) 0px 10px 20px -15px; */
  box-shadow: ${themeGet('shadows.elevation.shadow.overlay')};
  user-select: none;
  animation-duration: 400ms;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform, opacity;
  &[data-state='delayed-open'][data-side='top'] {
    animation-name: ${slideDownAndFade};
  }
  &[data-state='delayed-open'][data-side='right'] {
    animation-name: ${slideLeftAndFade};
  }
  &[data-state='delayed-open'][data-side='bottom'] {
    animation-name: ${slideUpAndFade};
  }
  &[data-state='delayed-open'][data-side='left'] {
    animation-name: ${slideRightAndFade};
  }
`
export const StyledTooltipArrow = styled(Tooltip.Arrow)`
  fill: ${themeGet('colors.elevation.surface.default.[default]')};
`
export const UnstyledButton = styled.button`
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;

  &:focus {
    outline-offset: 2px;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }
`
// custom styled
export const CustomStyledTooltipContent = styled(Tooltip.Content)`
  border-radius: ${themeGet('radii.2')};
  padding: 8px 16px;
  font-size: ${themeGet('fontSizes.3')};
  line-height: 1;
  color: ${themeGet('colors.text.accent.teal.bolder')};
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  border: 1px solid ${themeGet('colors.border.default')};
  /* box-shadow: hsl(206 22% 7% / 35%) 0px 10px 38px -10px, hsl(206 22% 7% / 20%) 0px 10px 20px -15px; */
  box-shadow: ${themeGet('shadows.elevation.shadow.overlay')};
  user-select: none;
  animation-duration: 400ms;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform, opacity;
  max-width: 376px;
  &[data-state='delayed-open'][data-side='top'] {
    animation-name: ${slideDownAndFade};
  }
  &[data-state='delayed-open'][data-side='right'] {
    animation-name: ${slideLeftAndFade};
  }
  &[data-state='delayed-open'][data-side='bottom'] {
    animation-name: ${slideUpAndFade};
  }
  &[data-state='delayed-open'][data-side='left'] {
    animation-name: ${slideRightAndFade};
  }
`
export const CustomStyledTooltipArrow = styled(Tooltip.Arrow)`
  fill: ${themeGet('colors.elevation.surface.default.[default]')};
  stroke: ${themeGet('colors.border.default')};
`
export const CustomStyledTooltipContentSlot = styled(Box)`
  border-radius: ${themeGet('radii.2')};
  padding: 8px 16px;
  font-size: ${themeGet('fontSizes.3')};
  line-height: 1;
  color: ${themeGet('colors.text.accent.teal.bolder')};
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  border: 1px solid ${themeGet('colors.border.default')};
  /* box-shadow: hsl(206 22% 7% / 35%) 0px 10px 38px -10px, hsl(206 22% 7% / 20%) 0px 10px 20px -15px; */
  box-shadow: ${themeGet('shadows.elevation.shadow.overlay')};
  user-select: none;
  animation-duration: 400ms;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform, opacity;
  max-width: 376px;
  &[data-state='delayed-open'][data-side='top'] {
    animation-name: ${slideDownAndFade};
  }
  &[data-state='delayed-open'][data-side='right'] {
    animation-name: ${slideLeftAndFade};
  }
  &[data-state='delayed-open'][data-side='bottom'] {
    animation-name: ${slideUpAndFade};
  }
  &[data-state='delayed-open'][data-side='left'] {
    animation-name: ${slideRightAndFade};
  }
`

// temp styles
export const StyledRadixTooltipPositionButtonTrigger = styled(Tooltip.Trigger)`
  margin: ${themeGet('space.2')};
  border: 1px solid ${themeGet('colors.border.default')};
  background: transparent;
`
export const StyledRadixTooltipTrigger = styled(Tooltip.Trigger)``
export const StyledRadixTooltipContent = styled(Tooltip.Content)`
  transform-origin: 'var(--radix-tooltip-content-transform-origin)';
  // ensures content isn't selectable
  // this is just a detterent to people putting interactive content inside a Tooltip
  user-select: none;
  //
  background-color: ${themeGet('colors.background.neutral.bold.default')};
  color: ${themeGet('colors.elevation.surface.default.[default]')};
  font-size: ${themeGet('fontSizes.3')};
  border-radius: ${themeGet('radii.2')};
  padding: ${themeGet('space.2')};
  max-width: 300px;
`
export const StyledRadixTooltipArrow = styled(Tooltip.Arrow)`
  fill: ${themeGet('colors.background.neutral.bold.default')};
`
export const StyledRadixTooltipContentSlot = styled(Box)`
  transform-origin: 'var(--radix-tooltip-content-transform-origin)';
  // ensures content isn't selectable
  // this is just a detterent to people putting interactive content inside a Tooltip
  user-select: none;
  //
  background-color: ${themeGet('colors.background.neutral.bold.default')};
  color: ${themeGet('colors.elevation.surface.default.[default]')};
  font-size: ${themeGet('fontSizes.3')};
  border-radius: ${themeGet('radii.2')};
  padding: ${themeGet('space.2')};
  max-width: 300px;
`
const scaleIn = keyframes`
  0% {
    opacity: 0; 
    transform: scale(0)
  }
  100% {
    opacity: 1; 
    transform: scale(1)
  }
`
const fadeIn = keyframes`
  0% {
    opacity: 0
  };
  100% {
    opacity: 1
  }
`
const fadeOut = keyframes`
  0% {
    opacity: 1
  }
  100% {
    opacity: 0
  }
`
export const StyledRadixAnimatedTooltip = styled(Tooltip)`
  transform-origin: 'var(--radix-tooltip-content-transform-origin)';
  // ensures content isn't selectable
  // this is just a detterent to people putting interactive content inside a Tooltip
  user-select: none;
  //
  background-color: ${themeGet('colors.background.neutral.bold.default')};
  color: ${themeGet('colors.elevation.surface.default.[default]')};
  font-size: ${themeGet('fontSizes.3')};
  border-radius: ${themeGet('radii.2')};
  padding: ${themeGet('space.2')};
  max-width: 300px;
  &[data-state='delayed-open'] {
    animation: ${scaleIn} 0.6s cubic-bezier(0.16, 1, 0.3, 1);
  }
  &[data-state='instant-open'] {
    animation: ${fadeIn} 0.2s ease-out;
  }
  &[data-state='closed'] {
    animation: ${fadeOut} 0.2s ease-out;
  }
`
export const StyledRadixAnimatedTooltipContent = styled(Tooltip.Content)`
  transform-origin: 'var(--radix-tooltip-content-transform-origin)';
  // ensures content isn't selectable
  // this is just a detterent to people putting interactive content inside a Tooltip
  user-select: none;
  //
  background-color: ${themeGet('colors.background.neutral.bold.default')};
  color: ${themeGet('colors.elevation.surface.default.[default]')};
  font-size: ${themeGet('fontSizes.3')};
  border-radius: ${themeGet('radii.2')};
  padding: ${themeGet('space.2')};
  max-width: 300px;
  &[data-state='delayed-open'] {
    animation: ${scaleIn} 0.6s cubic-bezier(0.16, 1, 0.3, 1);
  }
  &[data-state='instant-open'] {
    animation: ${fadeIn} 0.2s ease-out;
  }
  &[data-state='closed'] {
    animation: ${fadeOut} 0.2s ease-out;
  }
`
export const StyledGrid = styled(Box)`
  display: inline-grid;
  grid-template-columns: repeat(3, 50px);
  column-gap: 150;
  row-gap: 100;
  padding: 100;
  border: 1px solid ${themeGet('colors.border.default')};
`
export const StyledRadixTooltipChromaticContent = styled(Tooltip.Content)`
  box-sizing: border-box;
  display: grid;
  place-content: center;
  width: 64px;
  height: 64px;
  background-color: ${themeGet('colors.background.accent.blue.bolder')};
  color: ${themeGet('colors.elevation.surface.default.[default]')};
  font-size: 11px;
  border: 1px solid rgba(0, 0, 0, 0.3);
`
export const StyledRadixChromaticTrigger = styled(Tooltip.Trigger)`
  box-sizing: border-box;
  width: 32px;
  height: 32px;
  background-color: tomato;
  border: 1px solid rgba(0, 0, 0, 0.3);
`
export const StyledRadixChromaticArrow = styled(Tooltip.Arrow)`
  fill: black;
`
